import { z } from 'zod';
import { INDUSTRIES, USER_ROLES, USER_STATUS } from '../enums.js';
import { AddressSchema, EntityMetaSchema, zEmail, zEnum, zNonEmpty, zPercent, zPhone } from './common.js';

/** Business (tenant) profile + settings. */
export const BusinessHoursSchema = z.object({
  // 0=Sunday..6=Saturday; open/close as "HH:mm" 24h; closed days omit or set enabled:false
  day: z.number().int().min(0).max(6),
  enabled: z.boolean().default(true),
  open: z.string().regex(/^\d{2}:\d{2}$/).default('08:00'),
  close: z.string().regex(/^\d{2}:\d{2}$/).default('17:00'),
});

export const TaxRateSchema = z.object({
  id: z.string(),
  name: zNonEmpty.max(80),
  rate: zPercent, // e.g. 8.25
  isDefault: z.boolean().default(false),
});

export const TenantSettingsSchema = z.object({
  timezone: z.string().default('America/New_York'),
  currency: z.string().length(3).default('USD'),
  logoKey: z.string().optional(), // S3 key (private); served via presigned URL
  brandColor: z.string().regex(/^#[0-9a-fA-F]{6}$/).optional(),
  businessHours: z.array(BusinessHoursSchema).default([]),
  taxRates: z.array(TaxRateSchema).default([]),
  jobTypes: z.array(zNonEmpty.max(80)).default([]),
  serviceArea: z.object({
    postalCodes: z.array(z.string().max(20)).default([]),
    radiusMiles: z.number().min(0).max(500).optional(),
  }).default({ postalCodes: [] }),
  invoiceTerms: z.string().max(4000).optional(),
  estimateTerms: z.string().max(4000).optional(),
  invoiceDueDays: z.number().int().min(0).max(180).default(14),
  /** Stripe Connect account id for this tenant, if payments settle to their own account. When set,
   *  the Tax Center deep-links to Stripe's 1099-K tax forms for that account instead of guiding. */
  stripeConnectAccountId: z.string().max(60).optional(),
});
export type TenantSettings = z.infer<typeof TenantSettingsSchema>;

export const TenantSchema = EntityMetaSchema.extend({
  name: zNonEmpty.max(160),
  industry: zEnum(INDUSTRIES),
  email: zEmail,
  phone: zPhone.optional(),
  address: AddressSchema.optional(),
  settings: TenantSettingsSchema,
});
export type Tenant = z.infer<typeof TenantSchema>;

/** Self-serve signup input (creates tenant + owner user). */
export const SignupSchema = z.object({
  businessName: zNonEmpty.max(160),
  industry: zEnum(INDUSTRIES),
  ownerName: zNonEmpty.max(120),
  email: zEmail,
  phone: zPhone.optional(),
  password: z.string().min(10, 'Use at least 10 characters').max(128),
});
export type SignupInput = z.infer<typeof SignupSchema>;

/** Employee/user within a tenant. */
export const UserSchema = EntityMetaSchema.extend({
  name: zNonEmpty.max(120),
  email: zEmail,
  phone: zPhone.optional(),
  roles: z.array(zEnum(USER_ROLES)).min(1),
  status: zEnum(USER_STATUS).default('invited'),
  cognitoSub: z.string().optional(),
  skills: z.array(z.string().max(60)).default([]), // for future skill-matching
  avatarKey: z.string().optional(),
});
export type User = z.infer<typeof UserSchema>;

export const InviteUserSchema = z.object({
  name: zNonEmpty.max(120),
  email: zEmail,
  phone: zPhone.optional(),
  roles: z.array(zEnum(USER_ROLES)).min(1, 'Choose at least one role'),
  skills: z.array(z.string().max(60)).optional(),
});
export type InviteUserInput = z.infer<typeof InviteUserSchema>;

export const UpdateUserSchema = InviteUserSchema.partial().extend({
  status: zEnum(USER_STATUS).optional(),
});
export type UpdateUserInput = z.infer<typeof UpdateUserSchema>;
